import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { ApiKey, UserPublic } from "@/lib/types";
import { formatMoney } from "@/lib/format";

interface TopBarProps {
  title: string;
  subtitle: string;
  user: UserPublic;
  apiKey: ApiKey | null;
  onLogout: () => void;
}

export function TopBar({ title, subtitle, user, apiKey, onLogout }: TopBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // 与旧 setupUserMenu 一致：菜单外点击关、Esc 关。
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (ev: MouseEvent) => {
      const t = ev.target as Node;
      if (menuRef.current?.contains(t) || toggleRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => ev.key === "Escape" && setMenuOpen(false);
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const balance = "￥" + formatMoney(user.balance_cny || 0);
  const displayName = user.display_name || user.username || "Agentfly";
  const avatar = (displayName.match(/[A-Za-z0-9一-龥]/)?.[0] || "A").toUpperCase();
  void apiKey; // reserved: future per-key indicator on the topbar pill.

  return (
    <header className="c2-topbar">
      <div className="c2-topbar-title">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <div className="c2-topbar-right">
        <button className="c2-icon-btn c2-bell" type="button" aria-label="消息（暂无）">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 7H4c0-1 2-2 2-7z" />
            <path d="M10 20a2 2 0 0 0 4 0" />
          </svg>
          <i className="c2-bell-dot" aria-hidden="true" />
        </button>
        <a className="c2-pill" href="/#market" aria-label="文档">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 4.5h11A4 4 0 0 1 19 8.5v11H7.5A3.5 3.5 0 0 1 4 16V4.5z" />
            <path d="M4 16a3.5 3.5 0 0 1 3.5-3.5H19" />
          </svg>
          <span>文档</span>
        </a>
        <span className="c2-pill c2-pill-locale" aria-label="语言">
          <span className="c2-locale-flag" aria-hidden="true">🇨🇳</span>
          <span>ZH</span>
        </span>
        <span className="c2-pill c2-pill-balance" aria-label="余额">
          <span className="c2-pill-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
              <path d="M2.5 10h19" />
            </svg>
          </span>
          <span className="c2-pill-balance-label">余额</span>
          <strong>{balance}</strong>
        </span>
        <button
          className="c2-user-chip"
          type="button"
          aria-expanded={menuOpen}
          ref={toggleRef}
          onClick={(ev) => {
            ev.stopPropagation();
            setMenuOpen((v) => !v);
          }}
        >
          <span className="c2-avatar" aria-hidden="true">{avatar}</span>
          <span className="c2-user-meta">
            <strong>{displayName}</strong>
          </span>
          <svg className="c2-user-chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <div
          className={cn("c2-user-menu", !menuOpen && "hidden")}
          ref={menuRef}
          onClick={(ev) => ev.stopPropagation()}
        >
          <div className="c2-user-menu-head">
            <strong>{displayName}</strong>
            <small>{user.username}</small>
          </div>
          <a href="/">返回官网</a>
          <a href="mailto:hello@agentfly.net">联系支持</a>
          <button type="button" onClick={() => { setMenuOpen(false); onLogout(); }}>退出登录</button>
        </div>
      </div>
    </header>
  );
}
